import { Injectable } from '@angular/core'
import { DocumentNode } from 'apollo-link'
import { Apollo, QueryRef } from 'apollo-angular'
import gql from 'graphql-tag'
import { Observable } from 'rxjs'
import { map, startWith } from 'rxjs/operators'
import { postContexts } from 'src/logic/post-states'

export const _subscriptionFragment = gql`
  fragment SubmissionsPageSubscription on SubscriptionInterface {
    id
    state
    currentPeriodEndsAt
    nextCreditsIssuedAt
    nextCreditsExpireAt
    nextExpiringCreditsCount
    pastDueAt
    ... on LegacySubscription {
      quantity
      extraPostPrice
      extraVideoPrice
      planAddOns {
        id
        code
      }
      plan {
        id
        code
        words
      }
    }
  }
`

export const fragments: {
  customer: DocumentNode
  subscription: DocumentNode
  submission: DocumentNode
  post: DocumentNode
  user: DocumentNode
} = {
  customer: gql`
    fragment SubmissionsPageCustomer on Customer {
      id
      businessName
      state
      nextPostingTime
      queuedPostsCount
      postCreditsCount
      postsIncluded
      postCreditsCount
      postDeliveryMethod
      emailForPosts
      unusedFreePostCouponCount
      numberOfOldCredits
      numberOfOldPosts
      preferredWritersCount
      canRequestKeywords
      lastTopicsPurchaseDate
      assignedKeywordsCount
      wlVisible
      balance
      multipass
      subscription {
        ...SubmissionsPageSubscription
      }
      keywords {
        entries {
          id
          state
        }
        totalCount
      }
      requests {
        entries {
          id
          state
        }
        totalCount
      }
      account {
        id
        billingType
        cardType
        lastFour
      }
      multipass
    }
    ${_subscriptionFragment}
  `,
  subscription: _subscriptionFragment,
  submission: gql`
    fragment SubmissionsPageSubmission on Submission {
      id
      customerId
      request {
        id
        videoTitle
      }
      type
      state
      comments
      createdAt
      updatedAt
      purchasedAt
      sentToCustomerAt
      initialUrl
      editedUrl
      vimeoUrl
      expanded @client
      visibleForm @client
    }
  `,
  post: gql`
    fragment SubmissionsPagePost on Post {
      id
      customerId
      keywordId
      writerId
      title
      content
      wordCount
      state
      customerPublished
      sentToCustomerAt
      postedDate
      declinedDate
      withdrawnDate
      wlVisible
      writerPreferred
      writerBlocked
      submissionPosition
      lastViewedByAgencyAt
      lastViewedByWlUserAt
      wlUserFeedbackMessage
      pinned
      keyword {
        id
        phrase
        postLimit
        deadline
        kind
        reservedPostCreditsCount
        multipass
        multipassCreditReserved
        multipassContentPriceInCredits
        multipassContentPriceInCents
        multipassVideoPriceInCredits
        multipassVideoPriceInCents
        multipassAddonsPriceInCredits
        plusPhoto
        plusVideo
        numberOfPostsThatCanBePurchasedWithCredits
        queuedCount
        contentType {
          id
        }
        canUpdate: can(action: "update")
      }
      writer {
        id
        state
        slug
        shortDisplayName
        displayName
        avatar
        suspended
        canShow: can(action: "show")
      }
      user {
        id
        fullName
      }
      lockStatus
      extraPurchases {
        id
        purchasedAt
        extra {
          id
          code
        }
      }
      appRating {
        id
        value
      }
      wlRating {
        id
        value
      }
      appPostReview {
        id
        content
      }
      wlPostReview {
        id
        content
        action
        thumbs
      }
      selectedPostDeclineReasons {
        id
        reason
      }
      postDeclineComments {
        id
        comments
      }
      bpsEvents {
        id
        task {
          id
          code
        }
      }
      expanded @client
      canPostNow: can(action: "post_now")
      canDecline: can(action: "decline")
      canUpdateWlVisible: can(action: "update_wl_visible")
      canPin: can(action: "pin")
      canRequestEditing: can(action: "request_editing")
      canUpdateWlPostReview: can(action: "update_wl_post_review")
      canExportDocX: can(action: "docx")
    }
  `,
  user: gql`
    fragment SubmissionsUserFragment on User {
      id
      fullName
      role
      uiPreferences {
        reminderBetterSubmissions
      }
      account {
        id
        accountName
      }
      canCreateVideoRequest: can(action: "create", subject: "VideoRequest")
      canUpdateSubscription: can(action: "update", subject: "Subscription")
      canViewWhiteLabel: can(action: "view", subject: "white_label_profile")
    }
  `,
}

export interface Data {
  customer: Customer
}

export interface User {
  id: number
  fullName: string
  role: string
  uiPreferences: {
    reminderBetterSubmissions: boolean
  }
  account: {
    id: number
    accountName: string
  }
  canCreateVideoRequest: boolean
  canUpdateSubscription: boolean
  canViewWhiteLabel: boolean
}

export interface Customer {
  id: number
  businessName: string
  state: string
  queuedPostsCount: number
  nextPostingTime: Date
  postDeliveryMethod: 'hubspot' | 'wordpress' | 'alternate_email'
  emailForPosts: string
  numberOfOldCredits: number
  numberOfOldPosts: number
  postCounts: { [context: string]: number }
  subscription: Subscription
  postCreditsCount: number
  postsIncluded: number
  preferredWritersCount: number
  canRequestKeywords: boolean
  lastTopicsPurchaseDate: Date
  assignedKeywordsCount: number
  wlVisible: boolean
  balance: number
  multipass: boolean
  submissions: {
    totalCount: number
    entries: Submission[]
  }
  posts: {
    totalCount: number
    entries: Post[]
  }
  keywords: {
    entries: { id: number; state: string }[]
    totalCount: number
  }
  requests: {
    entries: { id: number; state: string }[]
    totalCount: number
  }
  lastViewedPurchasePage: number
  lastViewedPurchaseContext: string
  plan: {
    code: string
  }
  account: {
    id: number
    billingType: 'credit_card' | 'paypal'
    cardType: string
    lastFour: string
  }
}

export interface Subscription {
  id: number
  state: string
  quantity: number
  currentPeriodEndsAt: Date
  nextCreditsIssuedAt: Date
  nextCreditsExpireAt: Date
  nextExpiringCreditsCount: number
  pastDueAt: Date
  extraVideoPrice: number
  extraPostPrice: number
  planAddOns: {
    id: number
    code: string
  }[]
  plan: {
    id: number
    code: string
    words: number
  }
}

export interface Submission {
  id: number
  customerId: number
  request: {
    id: number
    videoTitle: string
  }
  comments: string
  type: string
  state: string
  initialUrl: string
  editedUrl: string
  vimeoUrl: string
  createdAt: Date
  updatedAt: Date
  purchasedAt: Date
  sentToCustomerAt: Date
  expanded: boolean
  visibleForm: string
}

export interface PostDeclineReason {
  id: number
  reason: string
  comments: string
}

export type PublishedState = 'all' | 'unpublished' | 'published'

export interface Post {
  id: number
  customerId: number
  keywordId: number
  writerId: number
  title: string
  content: string
  state: string
  customerPublished: boolean
  sentToCustomerAt: Date
  postedDate: Date
  declinedDate: Date
  withdrawnDate: Date
  submissionPosition: number
  keyword: Keyword
  writer: Writer
  user: User
  lockStatus: 'me' | 'colleague' | 'writer' | 'white_label' | 'unlocked'
  extraPurchases: ExtraPurchase[]
  writerBlocked: string
  writerPreferred: string
  expanded: boolean
  pinned: boolean
  wordCount: number
  lastViewedByWlUserAt: Date
  lastViewedByAgencyAt: Date
  wlUserFeedbackMessage: string
  wlVisible: boolean
  appRating: {
    id: number
    value: number
  }
  wlRating: {
    id: number
    value: number
  }
  appPostReview: {
    id: number
    content: string
  }
  wlPostReview: {
    id: number
    content: string
    action: 'approve' | 'reject'
    thumbs: 'up' | 'down'
  }
  selectedPostDeclineReasons: PostDeclineReason[]
  postDeclineComments: PostDeclineReason
  bpsEvents: {
    id: number
    task: {
      id: number
      code: string
    }
  }[]
  canPostNow: boolean
  canDecline: boolean
  canUpdateWlVisible: boolean
  canPin: boolean
  canRequestEditing: boolean
  canExportDocX: boolean
}

export interface Writer {
  id: number
  state: string
  slug: string
  shortDisplayName: string
  displayName: string
  avatar: string
  suspended: boolean
  canShow: boolean
}

export interface Keyword {
  id: number
  phrase: string
  postLimit: number
  deadline: Date
  kind: 'open' | 'deadline' | 'assigned' | 'proposed'
  reservedPostCreditsCount: number
  multipass: boolean
  multipassCreditReserved: number
  multipassContentPriceInCredits: number
  multipassContentPriceInCents: number
  multipassVideoPriceInCredits: number
  multipassVideoPriceInCents: number
  multipassAddonsPriceInCredits: number
  plusPhoto: boolean
  plusVideo: boolean
  numberOfPostsThatCanBePurchasedWithCredits: number
  queuedCount: number
  contentType: {
    id: number
  }
  canUpdate: boolean
}

export interface ExtraPurchase {
  id: number
  purchasedAt: Date
  extra: {
    id: number
    code: string
  }
}

export const SubmissionsQuery: DocumentNode = gql`
  query SubmissionsQuery(
    $customerId: ID!
    $states: [String]
    $search: String
    $postStates: [String]
    $page: Int
    $perPage: Int
    $order: String
    $postOrder: String
    $publishedState: String
  ) {
    customer(id: $customerId) {
      ...SubmissionsPageCustomer
      submissions(state: $states, page: $page, perPage: $perPage, order: $order) {
        totalCount
        entries {
          ...SubmissionsPageSubmission
        }
      }
      posts(
        state: $postStates
        search: $search
        page: $page
        perPage: $perPage
        order: $postOrder
        publishedState: $publishedState
      ) {
        totalCount
        entries {
          ...SubmissionsPagePost
        }
      }
    }
  }
  ${fragments.customer}
  ${fragments.submission}
  ${fragments.post}
`

export const userQuery = gql`
  query PostRequestEditsUserQuery {
    currentUser {
      ...SubmissionsUserFragment
    }
  }
  ${fragments.user}
`

export const contentTypeQuery = gql`
  query ContentType {
    contentTypes {
      id
      name
      wordMin
      wordMax
    }
  }
`

@Injectable({ providedIn: 'root' })
export class SubmissionQueries {
  lastQueryVariables: object = {}

  constructor(private apollo: Apollo) {}

  submissionsQuery({
    customerId,
    states,
    postStates,
    page,
    perPage,
    order,
    postOrder,
    publishedState,
  }: {
    customerId: number
    states?: string[]
    postStates?: string[]
    page?: number
    perPage?: number
    order?: string
    postOrder?: string
    publishedState?: PublishedState // For purchased posts
  }): QueryRef<Data, Record<string, any>> {
    let variables = {
      customerId,
      states: states,
      search: null,
      postStates: postStates,
      page: page || 1,
      perPage: perPage || 999,
      order: order,
      postOrder: postOrder,
      publishedState,
    }
    this.lastQueryVariables['SubmissionsQuery'] = variables

    return this.apollo.watchQuery<Data>({
      query: SubmissionsQuery,
      variables,
      notifyOnNetworkStatusChange: true,
      fetchPolicy: 'cache-and-network',
    })
  }

  submissionsSearchQuery({
    customerId,
    states,
    search,
    postStates,
    page,
    perPage,
    order,
    postOrder,
  }: {
    customerId: number
    states?: string[]
    search: string
    postStates?: string[]
    page?: number
    perPage?: number
    order?: string
    postOrder?: string
  }): QueryRef<Data, Record<string, any>> {
    const defaultStates = ['new', 'viewed', 'awaiting_editing', 'purchased']
    let defaultPostStates = ['declined', 'posted', 'withdrawn']
    defaultPostStates.push(...postContexts.purchaseable)

    let variables = {
      customerId,
      states: states || defaultStates,
      search: search,
      postStates: postStates || defaultPostStates,
      page: page || 1,
      perPage: perPage || 999,
      order: order,
      postOrder: postOrder,
    }
    this.lastQueryVariables['SubmissionsQuery'] = variables

    return this.apollo.watchQuery<Data>({
      query: SubmissionsQuery,
      variables,
      notifyOnNetworkStatusChange: true,
      fetchPolicy: 'cache-and-network',
    })
  }

  user(): Observable<User> {
    return this.apollo
      .watchQuery<{ currentUser: User }>({
        query: userQuery,
        variables: {},
        notifyOnNetworkStatusChange: true,
        fetchPolicy: 'cache-and-network',
      })
      .valueChanges.pipe(
        map(({ data }) => data && data.currentUser),
        startWith(null)
      )
  }

  contentTypes(): Observable<any> {
    return this.apollo
      .watchQuery<{ contentTypes: DocumentNode }>({
        query: contentTypeQuery,
        variables: {},
        notifyOnNetworkStatusChange: true,
        fetchPolicy: 'cache-and-network',
      })
      .valueChanges.pipe(map(({ data }) => data && data.contentTypes))
  }
}
